import React, { useState, useRef, useEffect } from 'react';
import { FiX, FiSend } from 'react-icons/fi';
import { FaRobot } from 'react-icons/fa';
import ReactMarkdown from 'react-markdown';
import rehypeHighlight from 'rehype-highlight';
import 'highlight.js/styles/github-dark.css'; // Using dark theme for code
import './ChatBot.css';

const ChatBot = ({ isOpen, onClose }) => {
    const [messages, setMessages] = useState([
        { id: 1, text: "Hi! I'm your AI assistant. How can I help you with your 42 projects today?", sender: 'bot' }
    ]);
    const [inputValue, setInputValue] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const messagesEndRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages, isOpen, isLoading]);

    const handleSend = async () => {
        if (!inputValue.trim()) return;

        const userText = inputValue;
        setInputValue("");

        // Add user message
        const userMsg = { id: Date.now(), text: userText, sender: 'user' };
        setMessages(prev => [...prev, userMsg]);
        setIsLoading(true);

        try {
            const response = await fetch('/api/chat', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ message: userText }),
                // credentials: 'include' // If we need cookie-based session persistence across origins
            });

            const data = await response.json();

            if (data.response) {
                const botMsg = { 
                    id: Date.now() + 1, 
                    text: data.response, 
                    sender: 'bot' 
                };
                setMessages(prev => [...prev, botMsg]);
            } else if (data.error) {
                const errorMsg = {
                    id: Date.now() + 1,
                    text: `Error: ${data.error}`,
                    sender: 'bot'
                };
                setMessages(prev => [...prev, errorMsg]);
            }
        } catch (error) {
            console.error('Chat error:', error);
            const errorMsg = {
                id: Date.now() + 1,
                text: "Sorry, I couldn't connect to the server. Please ensure the backend is running.",
                sender: 'bot'
            };
            setMessages(prev => [...prev, errorMsg]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault(); // Prevent form submission if any
            handleSend();
        }
    };

    if (!isOpen) return null;

    return (
        <div className="chatbot-container">
            <div className="chatbot-header">
                <h3><FaRobot /> AI Assistant</h3>
                <div className="header-actions">
                    <button type="button" className="close-chat-btn" onClick={onClose}>
                        <FiX size={20} />
                    </button>
                </div>
            </div>
            
            <div className="chatbot-messages">
                {messages.map((msg) => (
                    <div key={msg.id} className={`message ${msg.sender}`}>
                        {msg.sender === 'bot' ? (
                            <ReactMarkdown rehypePlugins={[rehypeHighlight]}>
                                {msg.text}
                            </ReactMarkdown>
                        ) : (
                            msg.text
                        )}
                    </div>
                ))}
                {isLoading && (
                    <div className="message bot">
                        <div className="typing-indicator">
                            <div className="typing-dot"></div>
                            <div className="typing-dot"></div>
                            <div className="typing-dot"></div>
                        </div>
                    </div>
                )}
                <div ref={messagesEndRef} />
            </div>

            <div className="chatbot-input-area">
                <input 
                    type="text" 
                    className="chatbot-input"
                    placeholder="Ask a question..."
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyPress={handleKeyPress}
                    disabled={isLoading}
                    autoFocus
                />
                <button 
                    type="button"
                    className="send-chat-btn" 
                    onClick={handleSend}
                    disabled={!inputValue.trim() || isLoading}
                >
                    <FiSend size={16} />
                </button>
            </div>
        </div>
    );
};

export default ChatBot;
